import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COMMENT_API_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseMutationClearOptions } from 'shared/types/tanstack-utility-types'
import { commentsKeys } from 'modules/comments/api/key-factory'
import type { UpdateCommentRequestType } from 'modules/comments/api/types'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import type { CommentsTypeEnum } from 'modules/comments/types'

interface UseUpdateCommentOptions extends UseMutationClearOptions<
  CommentInterface,
  Error,
  UpdateCommentRequestType
> {
  commentId: number
  commentsType: CommentsTypeEnum
  pageId: number
}

export function useUpdateComment({
  commentId,
  commentsType,
  pageId,
  ...options
}: UseUpdateCommentOptions): UseMutationResult<CommentInterface, Error, UpdateCommentRequestType> {
  const queryClient = useQueryClient()
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.put,
    CommentInterface,
    UpdateCommentRequestType
  >({
    method: RequestMethodsEnum.put,
  })

  return useMutation({
    mutationKey: commentsKeys.update(commentsType, pageId, commentId),
    mutationFn: async (body: UpdateCommentRequestType) =>
      fetcher(`${COMMENT_API_PREFIX}/${commentId}`, body),
    ...options,
    onSuccess: async (...args) => {
      await options.onSuccess?.(...args)
      await queryClient.refetchQueries(
        { queryKey: commentsKeys.resource(commentsType, pageId), fetchStatus: 'fetching' },
        { cancelRefetch: false },
      )
      await queryClient.invalidateQueries(
        { queryKey: commentsKeys.resource(commentsType, pageId) },
        { cancelRefetch: false },
      )
    },
  })
}
